import { BuildClass, clamp } from '../../../universal';
import { React, _ } from '../../lib';
import { CJSX } from './meta-types';
import { Modal } from './modal';
import { stubDiv, stubInput } from './stubs';
import { TextboxNumeric } from './textbox';
const RangeSliderComponent = (props, ref) => {
    const refOuter = React.useRef(stubDiv);
    const refTrack = React.useRef(stubDiv);
    const refInput = React.useRef(stubInput);
    const cacheRect = React.useRef(stubDiv.getBoundingClientRect());
    const refDragging = React.useRef(false);
    const [__, setDragging] = React.useState(false);
    const [focused, setFocused] = React.useState(false);
    const filterValueStep = React.useCallback((value) => {
        value = clamp(value, props.min, props.max);
        if (!props.step) {
            return value;
        }
        if (value == props.max) {
            return props.max;
        }
        const steps = Math.round((value - props.min) / props.step);
        return props.min + steps * props.step;
    }, [props.min, props.step, props.max]);
    const getPerc = React.useCallback((value) => {
        const perc = (value - props.min) / (props.max - props.min);
        return clamp(perc, 0, 1);
    }, [props.min, props.max]);
    const [perc, setPerc] = React.useState(() => getPerc(props.value));
    React.useEffect(() => {
        setPerc(getPerc(props.value));
    }, [props.value, props.min, props.max]);
    const setValue = React.useCallback((value) => {
        if (props.value != value) {
            props.onUpdate(value);
        }
    }, [props.value, props.onUpdate]);
    React.useEffect(() => {
        const observer = new ResizeObserver(() => {
            cacheRect.current = refTrack.current.getBoundingClientRect();
        });
        observer.observe(refTrack.current);
        return () => {
            observer.disconnect();
        };
    }, [refTrack.current]);
    const setDraggedValue = React.useCallback((coordX) => {
        const rect = cacheRect.current;
        if (!rect) {
            return;
        }
        const posX = clamp(coordX, rect.left, rect.right);
        const percentage = (posX - rect.left) / rect.width;
        const valueRaw = props.min + (props.max - props.min) * percentage;
        const value = filterValueStep(valueRaw);
        setValue(value);
        setPerc(getPerc(value));
    }, [cacheRect, props.min, props.max, setValue, setPerc, filterValueStep]);
    const setDraggingOff = React.useCallback(() => {
        if (refDragging.current) {
            refDragging.current = false;
            setDragging(false);
            const value = props.min + (props.max - props.min) * perc;
            setValue(filterValueStep(value));
        }
    }, [perc, refDragging.current, props.min, props.max]);
    React.useEffect(() => {
        document.addEventListener('pointerup', setDraggingOff);
        return () => {
            document.removeEventListener('pointerup', setDraggingOff);
        };
    }, [setDraggingOff]);
    const dragMove = React.useCallback((ev) => {
        if (refDragging.current) {
            setDraggedValue(ev.clientX);
        }
    }, [refDragging.current, setDraggedValue]);
    React.useEffect(() => {
        if (refDragging.current) {
            document.addEventListener('pointermove', dragMove);
            return () => {
                document.removeEventListener('pointermove', dragMove);
            };
        }
        return _.noop;
    }, [dragMove, refDragging.current]);
    const onUpdateText = React.useCallback((value) => {
        setValue(filterValueStep(value));
    }, [setValue, filterValueStep]);
    const evDragStart = React.useCallback((ev) => {
        cacheRect.current = refTrack.current.getBoundingClientRect();
        refDragging.current = true;
        refInput.current?.focus();
        setDragging(true);
        setDraggedValue(ev.clientX);
        ev.preventDefault();
        ev.stopPropagation();
    }, [refDragging.current, setDraggedValue]);
    const moveStep = React.useCallback((steps) => {
        let value;
        if (props.step) {
            value = props.value + steps * props.step;
        }
        else {
            const percentage = perc + 0.01 * steps;
            value = props.min + (props.max - props.min) * percentage;
        }
        value = filterValueStep(value);
        setValue(value);
        setPerc(getPerc(value));
    }, [
        props.step,
        props.value,
        perc,
        props.min,
        props.max,
        filterValueStep,
        setValue,
        setPerc,
        getPerc,
    ]);
    const evKeyPress = React.useCallback((e) => {
        const multiplier = 1 * (e.shiftKey ? 5 : 1) * (e.ctrlKey ? 2 : 1);
        if (e.which == 37) {
            moveStep(-multiplier);
        }
        else if (e.which == 39) {
            moveStep(+multiplier);
        }
    }, [moveStep]);
    const setFocusedOn = React.useCallback(() => {
        setFocused(true);
    }, [setFocused]);
    const setFocusedOff = React.useCallback(() => {
        setFocused(false);
    }, [setFocused]);
    React.useImperativeHandle(ref, () => ({
        getElement: () => refOuter.current,
        getInput: () => refInput.current,
        focus: () => {
            refInput.current.focus();
        },
    }));
    return (React.createElement("div", { className: "ui5 ui5-range", ref: refOuter },
        React.createElement(CJSX, { cond: props.showValueText ?? false },
            React.createElement(TextboxNumeric, { type: "text", className: "text", value: props.value, onUpdate: onUpdateText })),
        React.createElement("input", { ref: refInput, className: "focus-control", readOnly: true, onFocus: setFocusedOn, onBlur: setFocusedOff, onKeyDown: evKeyPress }),
        React.createElement("div", { className: BuildClass({
                'range-outer': true,
                dragging: refDragging.current,
                focused: focused,
            }), onPointerDown: evDragStart },
            React.createElement("div", { ref: refTrack, className: BuildClass({
                    track: true,
                    disabled: props.disabled ?? false,
                }) }),
            React.createElement("div", { className: "thumb", style: { left: `calc(${perc * 100}% - 14px)` } },
                React.createElement(CJSX, { cond: !props.hideTooltip },
                    React.createElement("div", { className: "tooltip" }, props.value)),
                React.createElement("div", { className: "thumb-inner" }))),
        React.createElement(CJSX, { cond: refDragging.current },
            React.createElement(Modal, null,
                React.createElement("div", { className: "ui5-range-dropzone" })))));
};
export const RangeSlider = React.forwardRef(RangeSliderComponent);
//# sourceMappingURL=range.js.map